import { splitGraphemes } from 'unicode-segmenter/grapheme'

const MAX_FILENAME_BYTES = 255

function formatDownloadTimestamp(downloadedAt: Date): string {
  return [
    downloadedAt.getFullYear(),
    downloadedAt.getMonth() + 1,
    downloadedAt.getDate(),
    downloadedAt.getHours(),
    downloadedAt.getMinutes(),
    downloadedAt.getSeconds(),
  ]
    .map((part, index) => (index === 0 ? String(part) : String(part).padStart(2, '0')))
    .join('')
}

function truncateUtf8(value: string, maxBytes: number, encoder: TextEncoder): string {
  let byteLength = 0
  let result = ''

  for (const segment of splitGraphemes(value)) {
    const segmentBytes = encoder.encode(segment).length
    if (byteLength + segmentBytes > maxBytes) break
    result += segment
    byteLength += segmentBytes
  }

  return result
}

/** Keep the original name first, then add the brand and browser-local download time. */
export function brandDownloadFilename(filename: string, downloadedAt = new Date()): string {
  const extensionIndex = filename.lastIndexOf('.')
  const hasExtension = extensionIndex > 0
  const extension = hasExtension ? filename.slice(extensionIndex) : ''
  const stem = hasExtension ? filename.slice(0, extensionIndex) : filename
  const suffix = ` - KIAO Voice - ${formatDownloadTimestamp(downloadedAt)}`
  const encoder = new TextEncoder()
  const extensionBudget = MAX_FILENAME_BYTES - encoder.encode(suffix).length
  const safeExtension = truncateUtf8(extension, extensionBudget, encoder)
  const stemBudget = extensionBudget - encoder.encode(safeExtension).length
  const safeStem = truncateUtf8(stem, stemBudget, encoder)

  return `${safeStem}${suffix}${safeExtension}`
}

/**
 * Trigger a browser download of in-memory string content as a file.
 *
 * Shared by every transcript download surface (batch console, anonymous trial,
 * transcript detail) so the blob / createObjectURL / revoke lifecycle lives in
 * one place — a Safari revoke-timing or download-attribute fix lands once, not
 * three times.
 */
export function downloadTextFile(content: string, filename: string, mime: string): void {
  downloadBlob(new Blob([content], { type: mime }), filename)
}

/** Trigger a browser download of an in-memory Blob (e.g. an exported audio clip). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = brandDownloadFilename(filename)
  document.body.append(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
